import React, { useEffect, useState } from 'react';
import { adminAPI, questionAPI } from '../../../api';

interface Props { section?: string; }

const AdminGeneral: React.FC<Props> = ({ section }) => {
  const [settings, setSettings] = useState<any>({});
  const [loading, setLoading] = useState(true);

  // 해설 PDF 매핑 상태
  const [pdfList, setPdfList] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [pdfForm, setPdfForm] = useState({ subject: '', exam_type: '필기', year: '', round: '', pdf_url: '' });
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    adminAPI.settings().then(res => { setSettings(res.data); setLoading(false); });
    // PDF 매핑 탭일 때 데이터 로드
    if (!section || section === 'pdf') {
      adminAPI.explanationPdfs().then(res => setPdfList(res.data)).catch(() => {});
      questionAPI.adminSubjects().then(res => setSubjects(res.data)).catch(() => {});
    }
  }, [section]);

  const loadPdfs = () => {
    adminAPI.explanationPdfs().then(res => setPdfList(res.data)).catch(() => {});
  };

  const addPdf = async () => {
    if (!pdfForm.subject || !pdfForm.year || !pdfForm.round || !pdfForm.pdf_url) {
      alert('모든 항목을 입력해주세요.'); return;
    }
    setPdfLoading(true);
    try {
      await adminAPI.createExplanationPdf({
        subject: +pdfForm.subject,
        exam_type: pdfForm.exam_type,
        year: +pdfForm.year,
        round: +pdfForm.round,
        pdf_url: pdfForm.pdf_url,
      });
      setPdfForm({ subject: '', exam_type: '필기', year: '', round: '', pdf_url: '' });
      loadPdfs();
    } catch (e: any) {
      alert(e?.response?.data?.non_field_errors?.[0] || '등록 실패');
    } finally { setPdfLoading(false); }
  };

  const deletePdf = async (id: number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await adminAPI.deleteExplanationPdf(id);
    loadPdfs();
  };

  const save = async () => {
    try { await adminAPI.updateSettings(settings); alert('저장되었습니다.'); } catch (e) { alert('저장 실패'); }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <span className="icon">{section === 'login' ? '🔑' : '⚙️'}</span>
          {section === 'login' ? '로그인 모드 설정' : section === 'questions' ? '문제 데이터 관리' : '일반 관리'}
        </div>
        <button className="btn btn-primary" onClick={save}>💾 저장</button>
      </div>

      {!section && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">🎯 체험 모의고사 설정</div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { key: 'trial_pilgi_enabled', label: '📘 필기 체험모드', desc: '비로그인 사용자 필기 100문제 체험' },
                { key: 'trial_silgi_enabled', label: '📙 실기 체험모드', desc: '비로그인 사용자 실기 20문제 체험' },
              ].map(({ key, label, desc }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--gray-50)', borderRadius: 10, border: `2px solid ${settings[key] ? '#2ecc71' : 'var(--border)'}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 48, height: 26, cursor: 'pointer', flexShrink: 0 }}>
                    <input type="checkbox" checked={!!settings[key]}
                      onChange={e => setSettings({ ...settings, [key]: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{ position: 'absolute', inset: 0, borderRadius: 26, transition: '0.3s', background: settings[key] ? '#2ecc71' : '#ccc' }}>
                      <span style={{ position: 'absolute', top: 3, left: settings[key] ? 26 : 3, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: '0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
              💡 OFF 시 체험 버튼 클릭하면 "준비 중" 메시지가 표시됩니다.
            </div>
          </div>
        </div>
      )}

      {!section && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">📝 폰트 크기 설정</div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {[
                { key: 'font_size_pilgi_normal', label: '📘 필기 일반' },
                { key: 'font_size_pilgi_mock', label: '📗 필기 모의' },
                { key: 'font_size_silgi_normal', label: '📙 실기 일반' },
                { key: 'font_size_silgi_mock', label: '📕 실기 모의' },
              ].map(({ key, label }) => (
                <div key={key} style={{ textAlign: 'center', background: 'var(--gray-50)', padding: '14px 12px', borderRadius: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)' }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                    <button
                      className="btn btn-danger"
                      style={{ borderRadius: '50%', width: 32, height: 32, padding: 0, fontSize: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => setSettings({...settings, [key]: Math.max(10, (settings[key]||17)-1)})}
                    >−</button>
                    <input
                      type="number"
                      className="form-control"
                      style={{ width: 72, textAlign: 'center', fontWeight: 700, fontSize: 16, padding: '6px 8px' }}
                      value={settings[key]||17}
                      onChange={e => setSettings({...settings, [key]: +e.target.value})}
                    />
                    <button
                      className="btn btn-success"
                      style={{ borderRadius: '50%', width: 32, height: 32, padding: 0, fontSize: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => setSettings({...settings, [key]: Math.min(30, (settings[key]||17)+1)})}
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 8, fontSize: 13, color: 'var(--text-muted)' }}>
              미리보기: <span style={{ fontSize: settings.font_size_pilgi_normal || 17 }}>전기기사 문제 텍스트 예시</span>
            </div>
          </div>
        </div>
      )}

      {section === 'login' && (
        <div className="card">
          <div className="card-header">로그인/회원가입 모드</div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">로그인 화면 방식</label>
              <div style={{ display: 'flex', gap: 12 }}>
                {[{value: 'basic', label: '기본형', desc: '현재 구조'}, {value: 'open', label: '오픈형', desc: '추후 설정'}].map(opt => (
                  <label key={opt.value} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 10, border: `2px solid ${settings.login_mode === opt.value ? 'var(--primary)' : 'var(--border)'}`, background: settings.login_mode === opt.value ? 'var(--primary-light)' : 'white', cursor: 'pointer' }}>
                    <input type="radio" name="login_mode" value={opt.value} checked={settings.login_mode === opt.value} onChange={() => setSettings({...settings, login_mode: opt.value})} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">회원가입 방식</label>
              <div style={{ display: 'flex', gap: 12 }}>
                {[{value: 'simple', label: 'Simple', desc: 'ID/PW/이메일'}, {value: 'normal', label: 'Normal', desc: '+이름/연락처'}].map(opt => (
                  <label key={opt.value} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 10, border: `2px solid ${settings.register_mode === opt.value ? 'var(--primary)' : 'var(--border)'}`, background: settings.register_mode === opt.value ? 'var(--primary-light)' : 'white', cursor: 'pointer' }}>
                    <input type="radio" name="register_mode" value={opt.value} checked={settings.register_mode === opt.value} onChange={() => setSettings({...settings, register_mode: opt.value})} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ 히어로 이미지 슬라이더 관리 */}
      {!section && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">🖼️ 메인 배너 이미지 설정</div>
          <div className="card-body">
            {/* 간격 설정 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>배너 전환 간격</label>
              <input type="number" className="form-control" min={3} max={30}
                value={settings.hero_interval || 5}
                onChange={e => setSettings({ ...settings, hero_interval: +e.target.value })}
                style={{ width: 80 }} />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>초 (3~30초)</span>
            </div>

            {/* 이미지 URL 목록 */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 14, fontWeight: 700, display: 'block', marginBottom: 6 }}>
                배너 이미지 목록 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(한 줄에 하나씩 입력 | 1개=전체, 2개=반반, 3개=3등분 자동 분할)</span>
              </label>
              <textarea className="form-control"
                rows={6}
                placeholder={'https://example.com/image1.jpg  ← 외부 URL\nslide1.jpg  ← 내부 이미지 (public/images/image_slider/ 폴더)\n/images/image_slider/slide2.jpg  ← 내부 경로 직접 지정'}
                value={settings.hero_images || ''}
                onChange={e => setSettings({ ...settings, hero_images: e.target.value })}
                style={{ fontSize: 14, fontFamily: 'monospace', resize: 'vertical' }} />
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                💡 <strong>외부 URL</strong>: http(s)://로 시작하는 주소<br/>
                💡 <strong>내부 이미지</strong>: 파일명만 입력 (예: <code>photo.jpg</code>) → <code>public/images/image_slider/</code> 폴더에 파일 저장 필요<br/>
                💡 <strong>내부 경로</strong>: /로 시작 (예: <code>/images/image_slider/photo.jpg</code>)
              </div>
            </div>

            {/* 미리보기 */}
            {settings.hero_images && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>미리보기</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {settings.hero_images.split('\n').map((url: string, i: number) => {
                    const trimmed = url.trim();
                    if (!trimmed) return null;
                    // 미리보기도 동일한 경로 변환 적용
                    const src = trimmed.startsWith('http://') || trimmed.startsWith('https://')
                      ? trimmed
                      : trimmed.startsWith('/') ? trimmed : `/images/image_slider/${trimmed}`;
                    return (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={src} alt={`슬라이드 ${i+1}`}
                          style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }}
                          onError={e => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                        <span style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10, padding: '1px 5px', borderRadius: 4 }}>{i+1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', lineHeight: 2 }}>
              💡 <strong>배너 자동 분할:</strong> 이미지 1개 → 전체 너비 / 2개 → 좌우 반반 / 3개 → 3등분<br/>
              💡 <strong>이미지 없을 때:</strong> 전기 회로도 SVG가 기본으로 표시됩니다.<br/>
              💡 <strong>권장 크기:</strong> 가로 1200px 이상, 세로 220px 내외 (비율 유지됨)<br/>
              💡 <strong>지원 형식:</strong> jpg, png, gif, webp 및 외부 URL
            </div>
          </div>
        </div>
      )}
      {/* ✅ 오른쪽(hero) 배너 이미지 설정 */}
      {!section && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">🖼️ Hero 오른쪽 배너 이미지 설정</div>
          <div className="card-body">
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 14, fontWeight: 700, display: 'block', marginBottom: 6 }}>
                오른쪽 배너 이미지 목록 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(1개=전체, 2개=반반, 3개=3등분 자동 분할)</span>
              </label>
              <textarea className="form-control"
                rows={4}
                placeholder={"https://example.com/image1.jpg  (외부 URL)\nslide1.jpg  (내부 이미지)\n/images/image_slider/slide2.jpg  (내부 경로)"}
                value={settings.hero_images2 || ''}
                onChange={e => setSettings({ ...settings, hero_images2: e.target.value })}
                style={{ fontSize: 14, fontFamily: 'monospace', resize: 'vertical' }} />
            </div>
            {/* 미리보기 */}
            {settings.hero_images2 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>미리보기</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {settings.hero_images2.split('\n').map((url: string, i: number) => {
                    const trimmed = url.trim();
                    if (!trimmed) return null;
                    const src = trimmed.startsWith('http://') || trimmed.startsWith('https://')
                      ? trimmed
                      : trimmed.startsWith('/') ? trimmed : `/images/image_slider/${trimmed}`;
                    return (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={src} alt={`오른쪽배너 ${i+1}`}
                          style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }}
                          onError={e => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                        <span style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10, padding: '1px 5px', borderRadius: 4 }}>{i+1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', lineHeight: 2 }}>
              💡 <strong>상단 배너와 독립적으로 관리</strong>됩니다. (최대 3개)<br/>
              💡 이미지 없을 때 → 전기 회로도 SVG가 표시됩니다.
            </div>
          </div>
        </div>
      )}

      {/* ✅ 해설 PDF 자동 연동 설정 */}
      {!section && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">📄 해설 PDF 자동 연동 설정</div>
          <div className="card-body">
            {/* 등록 폼 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px 60px 1fr auto', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <select className="form-control" value={pdfForm.subject}
                onChange={e => setPdfForm({ ...pdfForm, subject: e.target.value })}>
                <option value="">종목 선택</option>
                {subjects.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <select className="form-control" value={pdfForm.exam_type}
                onChange={e => setPdfForm({ ...pdfForm, exam_type: e.target.value })}>
                <option value="필기">필기</option>
                <option value="실기">실기</option>
              </select>
              <input className="form-control" type="number" placeholder="년도" value={pdfForm.year}
                onChange={e => setPdfForm({ ...pdfForm, year: e.target.value })} />
              <input className="form-control" type="number" placeholder="차수" value={pdfForm.round}
                onChange={e => setPdfForm({ ...pdfForm, round: e.target.value })} />
              <input className="form-control" type="text" placeholder="/media/pdf/파일명.pdf"
                value={pdfForm.pdf_url}
                onChange={e => setPdfForm({ ...pdfForm, pdf_url: e.target.value })} />
              <button className="btn btn-primary btn-sm" onClick={addPdf} disabled={pdfLoading}>
                {pdfLoading ? '...' : '+ 추가'}
              </button>
            </div>

            {/* 목록 테이블 */}
            {pdfList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
                등록된 해설 PDF 매핑이 없습니다.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--border)' }}>
                    {['종목', '유형', '년도', '차수', 'PDF URL', '삭제'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pdfList.map((p: any) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 10px' }}>{p.subject_name || p.subject}</td>
                      <td style={{ padding: '7px 10px' }}>{p.exam_type}</td>
                      <td style={{ padding: '7px 10px' }}>{p.year}</td>
                      <td style={{ padding: '7px 10px' }}>{p.round}</td>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.pdf_url}
                      </td>
                      <td style={{ padding: '7px 10px' }}>
                        <button className="btn btn-danger btn-sm" onClick={() => deletePdf(p.id)}>삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
              💡 PDF URL 예시: <code>/media/pdf/2022_2차_해설.pdf</code><br/>
              💡 서버에 PDF를 먼저 업로드한 후 URL을 등록하세요.<br/>
              💡 문제풀기 화면에서 년도/차수가 일치하면 자동으로 해설집이 열립니다.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGeneral;
